import {
    Component,
    ComponentRef,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChildren,
    ViewContainerRef
} from '@angular/core';
import {ActivatedRouteSnapshot, ActivationEnd, Router} from '@angular/router';

import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';

import {Platform, NavController} from '@ionic/angular';

import {TranslateService} from '@ngx-translate/core';
import {AppConfigService} from "./services/app-config.service";

// import * as moment from 'moment';

import {IdViewRefDirective} from './directives/id-viewref/id-viewref.directive';
import {CommunicationService} from './services/communication.service';
import {Storage} from '@ionic/storage-angular';
import {StateStorageService} from './services/state-storage.service';
import {MenuPage, appMenuPages} from './app.menu-pages';

import {AutoUnsubscribe} from './decorators/common.decorators';
import {AuthData} from './services/common-datatypes';
import {CommonFunctionsService} from './services/common-functions.service';

import * as introJs from 'intro.js';
import * as dayjs from "dayjs";

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {

    // template variables
    public appPages = appMenuPages;
    public activePage: MenuPage | undefined;
    public userName: string = '';

    // to activate menu options
//    @ViewChildren(IdViewRefDirective, {read: ViewContainerRef}) viewChildren!: QueryList<any>;
    @ViewChildren(IdViewRefDirective) viewChildren!: QueryList<any>;
    private lastMenuOptionViewRef: ViewContainerRef | undefined;
    private lastMenuOptionComponentRef: ComponentRef<any> | undefined;
    private lastUrl: string = '';

    // noinspection JSMismatchedCollectionQueryUpdate
    @AutoUnsubscribe('ngOnDestroy')
    private subscriptions: Subscription[] = [];

    constructor(
        private platform: Platform,
        private translate: TranslateService,
        private config: AppConfigService,
        private navController: NavController,
        private router: Router,
        public appState: StateStorageService,
        private appComm: CommunicationService,
        public commonFunctions: CommonFunctionsService,
        private storage: Storage) {
    }

    private initTranslation(): void {
        const defaultLang = this.config.getAppConfig('i18n.locale', 'de');
// TODO        this.translate.setTranslation('en', require('../assets/i18n/en.json'));
// TODO        this.translate.setTranslation('de', require('../assets/i18n/de.json'));
        this.translate.setDefaultLang(defaultLang);
        this.translate.use((this.appState.userLanguage) ? this.appState.userLanguage : defaultLang);

        // track language changes
        this.subscriptions.push(
            this.appComm.browserLanguage$.subscribe((value: string) => {
                if (this.appState.userLanguage) return;
                const browserLang = value.split(',')[0];
                if (this.translate.getLangs().includes(browserLang)) this.translate.use(browserLang);
                this.appState.locale = browserLang;
            })
        );
        this.subscriptions.push(
            this.appState.userLanguage$.subscribe((value: string) => {
                if (this.translate.getLangs().includes(value)) this.translate.use(value);
                this.appState.locale = value;
            })
        );
        this.subscriptions.push(
            this.appState.locale$.subscribe((value: string) => {
                dayjs.locale(value);
            })
        );
        this.subscriptions.push(
            this.appState.authData$.subscribe((value: AuthData) => {
                this.visibleMenuPages(value);
            })
        );
        this.subscriptions.push(
            this.appState.userRolesChange$.subscribe(() => {
                this.visibleMenuPages(this.appState.authData);
            })
        );

    }

    private navigationWatch(): void {
        // in angular we could subscribe to this.route.queryParams, but in ionic this is not working due to internal caching stack,
        // so we use this navigation Observable
        this.subscriptions.push(
            this.router.events
                .pipe(
                    // tap(event => { if (event instanceof ActivationEnd) console.log('router: ', event)}),
                    filter(event => event instanceof ActivationEnd))
                .subscribe((event) => {
                    // this hits several angular bugs like:
                    // https://github.com/angular/angular/issues/22842
                    // https://github.com/angular/angular/issues/16710
                    // so the workaround here is: we only have one level of pages, no children
                    // so the "false" navigation is detected as the empty "/" parent
                    if ((<ActivationEnd>event).snapshot.url.length > 0) {
                        this.appState.navigation = (<ActivationEnd>event).snapshot;
                    }
                })
        );
    }

    private menuControl(): void {
        this.subscriptions.push(
            this.appState.loggedIn$.subscribe((value: boolean) => {
                if (this.appState.loggedIn) {
                    this.appState.splitPaneHidden = false;
                    this.userName = this.appState.authData.realName ?? '';
                }
            })
        );

        this.subscriptions.push(
            this.appState.navigation$
                .subscribe((snapshot: ActivatedRouteSnapshot) => {

                    if (!snapshot || !snapshot.url) return;
                    const url = '/' + snapshot.url.toString();
                    if (this.lastUrl === url) return;
                    this.lastUrl = url;
                    const routedPage = this.appPages.find(
                        function (page) {
                            return (url === page.url);
                        }, url);
                    if (!routedPage) return;
                    this.activePage = routedPage;

                    if (!this.appState.splitPaneVisible) return;
                    // clear old menu option if set
                    if (this.lastMenuOptionComponentRef) this.lastMenuOptionComponentRef.destroy();
                    if (this.lastMenuOptionViewRef) {
                        this.lastMenuOptionViewRef.clear();
                    }
                    // set new menu option if available
                    if (!routedPage.option) return;
                    const activeChild: IdViewRefDirective = this.viewChildren.find(
                        (child) => child.id === 'menuOption' + routedPage.name
                    );
                    if (!activeChild) return;
                    this.lastMenuOptionComponentRef = activeChild.viewContainerRef.createComponent(routedPage.option);

                })
        );
    }

    private visibleMenuPages(auth: AuthData) {
        const visiblePages: MenuPage[] = [];
        appMenuPages.forEach((page: MenuPage) => {
            if (!page.access || this.commonFunctions.hasAuthRole(page.access, auth.role)) visiblePages.push(page);
        });
        this.appPages = visiblePages;
    }

    async ngOnInit() {
        this.initTranslation();
        this.navigationWatch();
        this.menuControl();
        await this.storage.create();
    }

    showHelp() {
        const introOptions = {
            nextLabel: 'weiter &rarr;',
            prevLabel: '&larr; zurück',
            skipLabel: '&times; Beenden',
            doneLabel: '&times; Fertig',
            hidePrev: true,
            hideNext: true,
            tooltipPosition: 'auto',
            scrollToElement: false,
            disableInteraction: true,
            exitOnOverlayClick: true,
            keyboardNavigation: true,
            exitOnEsc: true,
            showButtons: true,
            showStepNumbers: false,
            overlayOpacity: 0.2
        };
        const intro = (<any>introJs).introJs();
        intro.setOptions(introOptions);
        intro.start();
    }

    ngOnDestroy(): void {
    }

    public splitPaneVisible(value: boolean): void {
        this.appState.splitPaneVisible = value;
    }

}
